import { useEffect } from 'react';
import { DEFAULT_IMAGE, SITE_URL } from '../seo/site';

const WATCHMYEDIT_ID = `${SITE_URL}/#watchmyedit`;

const buildOrganization = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Freedman Labs',
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  description:
    'Video editing apps built by editors, for editors. Apps for post-production teams.',
});

const buildSoftwareApplication = (product) => {
  if (product.externalUrl) {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: product.title,
      description: product.app.description,
      applicationCategory: product.app.category,
      operatingSystem: 'Web',
      image: `${SITE_URL}${product.app.image}`,
      url: product.externalUrl,
      browserRequirements: 'Requires a modern web browser',
      author: {
        '@type': 'Organization',
        name: 'Freedman Labs',
        url: SITE_URL,
      },
    };
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: product.title,
    description: product.app.description,
    applicationCategory: product.app.category,
    operatingSystem: 'macOS',
    image: `${SITE_URL}${product.app.image}`,
    url: `${SITE_URL}/apps/${product.id}`,
    downloadUrl: product.app.appStoreUrl,
    offers: {
      '@type': 'Offer',
      price: product.app.price.replace('$', ''),
      priceCurrency: 'USD',
      url: product.app.appStoreUrl,
    },
    author: {
      '@type': 'Organization',
      name: 'Freedman Labs',
      url: SITE_URL,
    },
  };

  if (product.id === 'watchmyedit') {
    schema['@id'] = WATCHMYEDIT_ID;
    schema.featureList = [
      'Monitors video export folders for completed files',
      'Sends email alerts when exports finish',
      'Verifies files are fully written before notifying you',
      'Uploads finished videos to YouTube when you authorize Google account access',
      'Uploads finished videos to Frame.io, Vimeo, or PIX when enabled',
    ];
  }

  return schema;
};

const StructuredData = ({ content, product }) => {
  useEffect(() => {
    const payload = [buildOrganization()];

    if (product) {
      payload.push(buildSoftwareApplication(product));
    } else if (content?.products) {
      // Homepage structured data lives in public/index.html to avoid duplicate graphs.
      return undefined;
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-structured-data', 'true');
    script.textContent = JSON.stringify(payload.length === 1 ? payload[0] : payload);
    document.head.appendChild(script);

    return () => {
      document
        .querySelectorAll('script[data-structured-data="true"]')
        .forEach((node) => node.remove());
    };
  }, [content, product]);

  return null;
};

export default StructuredData;
